import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AuthFormShell from '../../components/AuthFormShell.jsx'
import PasswordChecklist, {
  isPasswordStrongEnough,
} from '../../components/PasswordChecklist.jsx'
import PasswordField from '../../components/PasswordField.jsx'
import Logo from '../../components/Logo.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import * as api from '../../db/api.js'
import './authTitam.css'

function TermsConsent({ id, checked, onChange, disabled }) {
  return (
    <label className="field-check" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        required
      />
      <span>
        Li e concordo com os{' '}
        <a href="/terms" target="_blank" rel="noopener noreferrer">
          Termos de Serviço
        </a>{' '}
        e com a{' '}
        <a href="/privacy" target="_blank" rel="noopener noreferrer">
          Política de Privacidade
        </a>
        .
      </span>
    </label>
  )
}

// Passo 2 do wizard: dados pessoais. Compartilhado entre os fluxos create e join
// (só mudam o prefixo dos ids, o rótulo do botão e o aviso do convite validado).
function PersonalFields({
  prefix,
  form,
  setField,
  busy,
  acceptedTerms,
  onAcceptTerms,
  onSubmit,
  error,
  submitLabel,
  notice,
  onChangeInvite,
}) {
  return (
    <form className="auth-form-wrap auth-form-nested auth-step-two" onSubmit={onSubmit}>
      <div className="auth-success auth-invite-ok">
        <span>{notice}</span>
        <button type="button" onClick={onChangeInvite} disabled={busy}>
          Alterar
        </button>
      </div>

      {error ? <div className="auth-error">{error}</div> : null}

      <div className="input-row">
        <div className="field">
          <label className="field-label" htmlFor={`${prefix}-first-name`}>
            Nome
          </label>
          <input
            id={`${prefix}-first-name`}
            className="input"
            value={form.firstName}
            onChange={setField('firstName')}
            placeholder="Joao"
            required
            disabled={busy}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor={`${prefix}-last-name`}>
            Sobrenome
          </label>
          <input
            id={`${prefix}-last-name`}
            className="input"
            value={form.lastName}
            onChange={setField('lastName')}
            placeholder="Silva"
            required
            disabled={busy}
          />
        </div>
      </div>

      <div className="input-row">
        <div className="field">
          <label className="field-label" htmlFor={`${prefix}-email`}>
            E-mail
          </label>
          <input
            id={`${prefix}-email`}
            className="input"
            type="email"
            value={form.email}
            onChange={setField('email')}
            placeholder="joao@empresa.com.br"
            required
            autoComplete="email"
            disabled={busy}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor={`${prefix}-phone`}>
            Telefone
          </label>
          <input
            id={`${prefix}-phone`}
            className="input"
            type="tel"
            value={form.phone}
            onChange={setField('phone')}
            placeholder="+55 11 91234-5678"
            required
            autoComplete="tel"
            disabled={busy}
          />
        </div>
      </div>

      <PasswordField
        id={`${prefix}-password`}
        label="Senha"
        value={form.password}
        onChange={setField('password')}
        placeholder="Minimo 8 caracteres"
        autoComplete="new-password"
        required
        disabled={busy}
      />
      <PasswordChecklist password={form.password} />

      <TermsConsent
        id={`${prefix}-terms`}
        checked={acceptedTerms}
        onChange={onAcceptTerms}
        disabled={busy}
      />

      <button className="btn btn-full auth-titam-submit" type="submit" disabled={busy}>
        {submitLabel}
      </button>
    </form>
  )
}

function normalizeInviteCode(code) {
  return code.trim().toUpperCase()
}

// Telefone internacional: mantém apenas dígitos e um "+" inicial opcional
// (formato E.164). Validação leve — exige ao menos 8 dígitos.
function normalizePhone(value) {
  const trimmed = value.trim()
  const hasPlus = trimmed.startsWith('+')
  const digits = trimmed.replace(/\D/g, '')
  return hasPlus ? `+${digits}` : digits
}

function isPhoneValid(value) {
  const digits = value.replace(/\D/g, '')
  return digits.length >= 8 && digits.length <= 15
}

export default function Register() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { signUp, signUpForJoin, validateInvite } = useAuth()

  // Modo de cadastro:
  //   'create' → Fluxo 1 (atual, INTOCADO): access_invite + cria organização.
  //   'join'   → Fluxo 2 (novo): convite de organização → entra em org existente.
  const [mode, setMode] = useState(
    searchParams.get('mode') === 'join' ? 'join' : 'create'
  )

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
  })

  // ---- Estado do Fluxo 1 (create) — inalterado ----
  const [inviteCodeInput, setInviteCodeInput] = useState(
    searchParams.get('invite') ?? searchParams.get('code') ?? ''
  )
  const [inviteState, setInviteState] = useState({
    loading: false,
    checkedCode: '',
    valid: false,
    data: null,
    error: '',
  })
  const [error, setError] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading] = useState(false)

  // ---- Estado do Fluxo 2 (join) ----
  const [orgCodeInput, setOrgCodeInput] = useState('')
  const [joinState, setJoinState] = useState({
    loading: false,
    checkedCode: '',
    valid: false,
    orgName: null,
    error: '',
  })
  const [joinError, setJoinError] = useState('')
  const [joinLoading, setJoinLoading] = useState(false)

  const passwordStrongEnough = useMemo(
    () => isPasswordStrongEnough(form.password),
    [form.password]
  )

  const setField = (field) => (event) =>
    setForm((previous) => ({ ...previous, [field]: event.target.value }))

  // === Fluxo 1 (create): validação de access_invite — INALTERADO ===
  useEffect(() => {
    const codeFromUrl = searchParams.get('invite') ?? searchParams.get('code') ?? ''
    if (!codeFromUrl) {
      setInviteState({
        loading: false,
        checkedCode: '',
        valid: false,
        data: null,
        error: '',
      })
      return
    }

    const normalizedCode = normalizeInviteCode(codeFromUrl)
    setInviteCodeInput(normalizedCode)

    let cancelled = false

    async function runValidation() {
      setInviteState((previous) => ({
        ...previous,
        loading: true,
        checkedCode: normalizedCode,
        error: '',
      }))

      const { data, error: inviteError } = await validateInvite(normalizedCode)

      if (cancelled) {
        return
      }

      const invite = Array.isArray(data) ? data[0] : null
      const isValid = !!invite?.is_valid

      setInviteState({
        loading: false,
        checkedCode: normalizedCode,
        valid: isValid,
        data: invite,
        error:
          inviteError?.message ||
          (isValid ? '' : 'Esse convite e invalido, expirou ou ja foi utilizado.'),
      })
    }

    void runValidation()

    return () => {
      cancelled = true
    }
  }, [searchParams, validateInvite])

  async function handleInviteValidation(event) {
    event.preventDefault()
    setError('')

    const normalizedCode = normalizeInviteCode(inviteCodeInput)

    if (!normalizedCode) {
      setInviteState((previous) => ({
        ...previous,
        valid: false,
        data: null,
        error: 'Informe um codigo de convite para continuar.',
      }))
      return
    }

    setSearchParams({ invite: normalizedCode })
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setError('')

    if (!inviteState.valid || !inviteState.checkedCode) {
      setError('Voce precisa entrar com um convite valido para criar a conta.')
      return
    }

    if (!acceptedTerms) {
      setError('Aceite os Termos de Serviço e a Política de Privacidade para criar a conta.')
      return
    }

    if (!isPhoneValid(form.phone)) {
      setError('Informe um telefone valido com DDD (ex.: +55 11 91234-5678).')
      return
    }

    if (!passwordStrongEnough) {
      setError('A senha precisa cumprir todos os requisitos exibidos abaixo do campo.')
      return
    }

    setLoading(true)
    const fullName = `${form.firstName} ${form.lastName}`.trim()
    const normalizedCode = normalizeInviteCode(inviteState.checkedCode)
    const normalizedPhone = normalizePhone(form.phone)
    const { data, error: authError } = await signUp(
      form.email,
      form.password,
      fullName,
      normalizedCode,
      normalizedPhone
    )

    if (authError) {
      console.error('[auth] signUp falhou:', {
        name: authError.name,
        status: authError.status,
        code: authError.code,
        message: authError.message,
      })
      setError(authError.message || 'Erro ao criar conta')
      setLoading(false)
      return
    }

    setLoading(false)

    if (data.session) {
      navigate('/onboarding')
      return
    }

    navigate('/login', {
      state: {
        signupDone: true,
      },
    })
  }

  // === Fluxo 2 (join): validação do convite de organização ===
  async function handleOrgCodeValidation(event) {
    event.preventDefault()
    setJoinError('')

    const normalizedCode = normalizeInviteCode(orgCodeInput)
    if (!normalizedCode) {
      setJoinState({
        loading: false,
        checkedCode: '',
        valid: false,
        orgName: null,
        error: 'Informe o codigo do convite para continuar.',
      })
      return
    }

    setJoinState((prev) => ({ ...prev, loading: true, error: '' }))
    try {
      const res = await api.previewOrgInvite(normalizedCode)
      setJoinState({
        loading: false,
        checkedCode: normalizedCode,
        valid: !!res?.valid,
        orgName: res?.organization?.name ?? null,
        error: res?.valid
          ? ''
          : 'Convite invalido, expirado, revogado ou ja utilizado.',
      })
    } catch (err) {
      setJoinState({
        loading: false,
        checkedCode: normalizedCode,
        valid: false,
        orgName: null,
        error: err.message || 'Falha ao validar o convite.',
      })
    }
  }

  async function handleJoinSubmit(event) {
    event.preventDefault()
    setJoinError('')

    if (!joinState.valid || !joinState.checkedCode) {
      setJoinError('Valide um convite de organizacao valido antes de criar a conta.')
      return
    }
    if (!acceptedTerms) {
      setJoinError('Aceite os Termos de Serviço e a Política de Privacidade para criar a conta.')
      return
    }
    if (!isPhoneValid(form.phone)) {
      setJoinError('Informe um telefone valido com DDD (ex.: +55 11 91234-5678).')
      return
    }
    if (!passwordStrongEnough) {
      setJoinError('A senha precisa cumprir todos os requisitos exibidos abaixo do campo.')
      return
    }

    setJoinLoading(true)
    const fullName = `${form.firstName} ${form.lastName}`.trim()
    const normalizedPhone = normalizePhone(form.phone)
    const { data, error: authError } = await signUpForJoin(
      form.email,
      form.password,
      fullName,
      normalizedPhone,
      joinState.checkedCode
    )

    if (authError) {
      console.error('[auth] signUpForJoin falhou:', {
        name: authError.name,
        status: authError.status,
        message: authError.message,
      })
      setJoinError(authError.message || 'Erro ao criar conta')
      setJoinLoading(false)
      return
    }

    setJoinLoading(false)

    // Com sessão imediata → /onboarding executa o join (lê org_invite_code do
    // metadata). Sem sessão (confirmação de e-mail) → login depois cai em
    // /onboarding (sem org) e o join roda lá.
    if (data.session) {
      navigate('/onboarding')
      return
    }
    navigate('/login', { state: { signupDone: true } })
  }

  function switchMode(next) {
    setMode(next)
    setError('')
    setJoinError('')
  }

  // "Alterar" no passo 2: volta ao passo 1 invalidando o convite atual.
  function changeInvite() {
    setError('')
    setSearchParams({})
  }

  function changeJoinInvite() {
    setJoinError('')
    setJoinState({ loading: false, checkedCode: '', valid: false, orgName: null, error: '' })
  }

  return (
    <AuthFormShell
      showcaseVariant="titam"
      showcaseTitle="Crie ou entre em uma organização."
      showcaseDescription="Crie sua conta com um convite e monte sua organização, ou use um convite de equipe para entrar em uma organização que já existe."
    >
      <div className="auth-form-wrap auth-titam-form">
        <span className="auth-form-logo">
          <Logo height={14} />
        </span>
        <h1 className="auth-title">Criar conta</h1>

        {/* Seletor de fluxo */}
        <div className="auth-mode-switch" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'create'}
            className={`auth-mode-btn ${mode === 'create' ? 'sel' : ''}`}
            onClick={() => switchMode('create')}
          >
            Criar minha organizacao
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'join'}
            className={`auth-mode-btn ${mode === 'join' ? 'sel' : ''}`}
            onClick={() => switchMode('join')}
          >
            Entrar em uma organizacao
          </button>
        </div>

        {/* ===================== FLUXO 1 — CREATE ===================== */}
        {mode === 'create' ? (
          inviteState.valid ? (
            <PersonalFields
              prefix="register"
              form={form}
              setField={setField}
              busy={loading}
              acceptedTerms={acceptedTerms}
              onAcceptTerms={(event) => setAcceptedTerms(event.target.checked)}
              onSubmit={handleSubmit}
              error={error}
              submitLabel={loading ? 'Criando conta...' : 'Criar conta com convite'}
              notice="Convite validado com sucesso."
              onChangeInvite={changeInvite}
            />
          ) : (
            <>
              <p className="auth-desc">
                Para entrar agora, voce precisa de um codigo de convite valido.
              </p>

              <form className="auth-invite-form" onSubmit={handleInviteValidation}>
                <div className="field">
                  <label className="field-label" htmlFor="invite-code">
                    Codigo de convite
                  </label>
                  <input
                    id="invite-code"
                    className="input invite-code-input"
                    value={inviteCodeInput}
                    onChange={(event) => setInviteCodeInput(event.target.value.toUpperCase())}
                    placeholder="EXEMPLO1234"
                    autoCapitalize="characters"
                    spellCheck="false"
                  />
                </div>

                <button className="btn btn-outline btn-full" type="submit" disabled={inviteState.loading}>
                  {inviteState.loading ? 'Validando convite...' : 'Validar convite'}
                </button>
              </form>

              {inviteState.error ? <div className="auth-error">{inviteState.error}</div> : null}
            </>
          )
        ) : joinState.valid ? (
          /* ===================== FLUXO 2 — JOIN ===================== */
          <PersonalFields
            prefix="join"
            form={form}
            setField={setField}
            busy={joinLoading}
            acceptedTerms={acceptedTerms}
            onAcceptTerms={(event) => setAcceptedTerms(event.target.checked)}
            onSubmit={handleJoinSubmit}
            error={joinError}
            submitLabel={joinLoading ? 'Criando conta...' : 'Criar conta e entrar'}
            notice={
              <>
                Convite valido. Voce vai entrar na organizacao
                {joinState.orgName ? <strong> {joinState.orgName}</strong> : ' existente'}.
              </>
            }
            onChangeInvite={changeJoinInvite}
          />
        ) : (
          <>
            <p className="auth-desc">
              Informe o codigo de convite que voce recebeu do administrador da
              organizacao. Sua conta tera acesso aos clientes dela.
            </p>

            <form className="auth-invite-form" onSubmit={handleOrgCodeValidation}>
              <div className="field">
                <label className="field-label" htmlFor="org-invite-code">
                  Codigo do convite
                </label>
                <input
                  id="org-invite-code"
                  className="input invite-code-input"
                  value={orgCodeInput}
                  onChange={(event) => setOrgCodeInput(event.target.value.toUpperCase())}
                  placeholder="ABCD-1234-EFGH"
                  autoCapitalize="characters"
                  spellCheck="false"
                />
              </div>

              <button className="btn btn-outline btn-full" type="submit" disabled={joinState.loading}>
                {joinState.loading ? 'Validando convite...' : 'Validar convite'}
              </button>
            </form>

            {joinState.error ? <div className="auth-error">{joinState.error}</div> : null}
          </>
        )}

        <div className="auth-switch">
          Ja tem conta?{' '}
          <button type="button" onClick={() => navigate('/login')}>
            Entrar
          </button>
        </div>
      </div>
    </AuthFormShell>
  )
}
